/**
 * API 공통 유틸리티 및 헬퍼 함수
 */

const getApiHost = (): string => {
  const envUrl = import.meta.env.VITE_DJANGO_API_URL as string | undefined;
  if (envUrl) {
    return envUrl.replace(/\/$/, "");
  }
  if (typeof window === "undefined") {
    return "http://localhost:8000";
  }
  const hostname = window.location.hostname;
  const protocol = window.location.protocol;
  const apiPort = hostname.includes("iptime") ? 48000 : 8000;
  return `${protocol}//${hostname}:${apiPort}`;
};

export const apiHost = getApiHost();

/** django API에 연결할 수 없을 때 사용자 안내 (로그인·메인 공통 톤) */
export const API_UNREACHABLE_MESSAGE =
  "서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.";

export type ApiErrorSetter = (
  message: string,
  isConnectionFailure?: boolean,
) => void;

/**
 * django API 연결 실패 여부.
 * - 네트워크 오류·타임아웃·응답 없음 → true
 * - 502/503/504 → true
 * - 일반 500·400·401/403 등 → false (API는 도달한 상태 또는 업무/내부 오류)
 */
export function isApiConnectionFailure(error: unknown): boolean {
  const err = error as {
    code?: string;
    response?: { status?: number };
    message?: string;
  } | null | undefined;

  if (!err) {
    return true;
  }

  const status = err.response?.status;
  if (status === 502 || status === 503 || status === 504) {
    return true;
  }
  if (status !== undefined) {
    return false;
  }

  // 응답 없음: 네트워크 단절, CORS, 타임아웃 등
  if (
    err.code === "ECONNABORTED" ||
    err.code === "ERR_NETWORK" ||
    err.code === "ETIMEDOUT" ||
    err.code === "ECONNREFUSED"
  ) {
    return true;
  }

  return true;
}

/**
 * 에러 응답 데이터가 요청 페이로드의 필드를 포함하는지 확인
 */
export function isPayloadError(data: object, payload: object): boolean {
  return Object.keys(data).every((key) => key in payload);
}

/**
 * 공통 타임아웃 에러 메시지 반환
 */
export function getErrorMessage(error: any): string {
  console.log("Error object:", error);
  if (isApiConnectionFailure(error)) {
    if (error?.code === "ECONNABORTED") {
      return "요청 시간이 초과되었습니다. 다시 시도해주세요.";
    }
    return API_UNREACHABLE_MESSAGE;
  }
  const data = error.response?.data;
  const details = data?.details;
  if (typeof details === "string") return details;
  if (details && typeof details === "object") {
    const firstMessage = Object.values(details)
      .flat()
      .find((value) => typeof value === "string");
    if (typeof firstMessage === "string") return firstMessage;
  }
  if (typeof data?.error === "string") return data.error;
  if (typeof data?.message === "string") return data.message;
  return "네트워크 오류입니다. 잠시 후 다시 시도하세요";
}
